import { useEffect, useState } from "react";
import TrustBadges from "../components/TrustBadges";
import { stockAvailability } from "../utils/stockAvailability";

function ListingDetailPage({
  error,
  isAuthenticated,
  isLoading,
  isOwner,
  listing,
  onBack,
  onEdit,
  onContactSeller,
  onOpenSellerProfile,
  onReportListing,
  onToggleFavorite,
  onMarkSold,
  onPublish,
  onRate,
  rating,
  ratingError,
  isRating,
  isContacting,
  contactError,
  favoriteActionListingId,
  isReportingListing,
  listingReportError,
  listingReportNotice,
  improvements,
  dismissedImprovementKeys,
  onDismissImprovement,
}) {
  const [selectedImage, setSelectedImage] = useState("");
  const detailImages = listing?.images || [];
  const selectedImageIndex = Math.max(0, detailImages.indexOf(selectedImage));

  function showAdjacentImage(direction) {
    if (detailImages.length < 2) return;
    const nextIndex = (selectedImageIndex + direction + detailImages.length) % detailImages.length;
    setSelectedImage(detailImages[nextIndex]);
  }

  useEffect(() => {
    setSelectedImage(listing?.images?.[0] || listing?.image || "");
  }, [listing?.id, listing?.image, listing?.images]);

  if (isLoading) {
    return <section className="main-panel"><div className="empty-results">Henter annonce...</div></section>;
  }

  if (!listing) {
    return (
      <section className="main-panel">
        <div className="empty-results">
          <strong>Annonce ikke fundet</strong>
          <p>{error || "Gå tilbage og vælg en annonce igen."}</p>
        </div>
      </section>
    );
  }

  const availability = stockAvailability(listing.inventoryCount);

  return (
    <section className="main-panel">
      <div className="page-heading detail-heading">
        <div>
          <span className="mini-label">{statusLabel(listing.status)}</span>
          <h1>{listing.title}</h1>
          <p>{listing.location || "Lokation ikke angivet"} | {listing.category}</p>
        </div>
        <button className="secondary-action page-action" onClick={onBack} type="button">
          Tilbage
        </button>
      </div>

      <div className="detail-layout">
        <div className="detail-gallery">
          <div className="detail-image">
          {selectedImage ? (
            <img src={selectedImage} alt={listing.title} />
          ) : (
            <div className="detail-image-placeholder">Intet billede</div>
          )}
          {detailImages.length > 1 && (
            <>
              <button className="detail-image-arrow previous" aria-label="Forrige billede" onClick={() => showAdjacentImage(-1)} type="button">‹</button>
              <button className="detail-image-arrow next" aria-label="Næste billede" onClick={() => showAdjacentImage(1)} type="button">›</button>
              <span className="detail-image-count">{selectedImageIndex + 1} / {detailImages.length}</span>
            </>
          )}
          </div>
          {listing.images?.length > 1 && (
            <div className="detail-thumbnails" aria-label="Annoncebilleder">
              {listing.images.map((image, index) => (
                <button className={selectedImage === image ? "active" : ""} key={image} onClick={() => setSelectedImage(image)} type="button">
                  <img src={image} alt={`${listing.title}, billede ${index + 1}`} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="detail-card">
          <div className="detail-price">{listing.price}</div>
          <p>{listing.description || "Ingen beskrivelse endnu."}</p>

          <div className="detail-facts">
            <span>Kategori</span>
            <strong>{listing.category}</strong>
            <span>Stand</span>
            <strong>{listing.condition || "Ikke angivet"}</strong>
            {availability && <><span>Tilgængelighed</span><strong className={`stock-label ${availability.level}`}>{availability.label}</strong></>}
            <span>AI score</span>
            <strong>{listing.aiScore || "-"}</strong>
          </div>

          {isOwner && (
            <section className="rating-card">
              <div className="rating-card-header">
                <div>
                  <span className="mini-label">Annonce-rating</span>
                  <h2>{rating ? `${rating.score}/100` : listing.aiScore ? `${listing.aiScore}/100` : "Ikke vurderet"}</h2>
                </div>
                <button disabled={isRating} onClick={() => onRate(listing)} type="button">
                  {isRating ? "Vurderer..." : "Vurder igen"}
                </button>
              </div>

              {ratingError && <p className="status-line error">{ratingError}</p>}

              {rating && (
                <>
                  <div className="rating-checks">
                    <RatingCheck label="Titel" status={rating.title_status} />
                    <RatingCheck label="Beskrivelse" status={rating.description_status} />
                    <RatingCheck label="Billeder" status={rating.image_status} />
                    <RatingCheck label="Kategori" status={rating.category_status} />
                    <RatingCheck label="Pris" status={rating.price_status} />
                    <RatingCheck label="Lokation" status={rating.contact_status} />
                  </div>

                </>
              )}

              {improvements && (
                <div className="improvement-list">
                  <strong>Forbedringsforslag</strong>
                  {visibleSuggestions(improvements.suggestions, dismissedImprovementKeys).length === 0 ? (
                    <p>Der er ingen yderligere forslag lige nu.</p>
                  ) : (
                    visibleSuggestions(improvements.suggestions, dismissedImprovementKeys).map((suggestion) => (
                      <article key={`${suggestion.field}-${suggestion.message}`}>
                        <span>{fieldLabel(suggestion.field)}</span>
                        <strong>{suggestion.message}</strong>
                        <p>{suggestion.reason}</p>
                        {suggestion.suggested_value && (
                          <div className="suggested-value">
                            <span>Forslag til tekst</span>
                            <p>{suggestion.suggested_value}</p>
                          </div>
                        )}
                        <div className="improvement-actions">
                          <button onClick={() => onEdit(listing)} type="button">
                            Ret annonce
                          </button>
                          <button onClick={() => onDismissImprovement(suggestion)} type="button">
                            Afvis
                          </button>
                        </div>
                      </article>
                    ))
                  )}
                </div>
              )}
            </section>
          )}

          <div className="detail-seller">
            <div className="detail-seller-identity">
              {listing.sellerLogo ? <img src={listing.sellerLogo} alt={`${listing.seller} logo`} /> : <div className="detail-seller-avatar">{initials(listing.seller)}</div>}
              <div>
                <span className={`seller-kind ${listing.sellerRole === "shop_owner" ? "shop" : "private"}`}>
                  {listing.sellerRole === "shop_owner" ? "Butik" : "Privat sælger"}
                </span>
                <strong>{listing.seller || "Ukendt sælger"}</strong>
                <p>{listing.sellerRole === "shop_owner" ? "Professionel butik på Annoncen" : "Privat bruger på Annoncen"}</p>
              </div>
            </div>
            <p>{sellerTrustText(listing)}</p>
            <TrustBadges
              completedSalesCount={listing.completedSalesCount}
              ratingAverage={listing.ratingAverage}
              ratingCount={listing.ratingCount}
              verified={listing.verified}
            />
            {!isOwner && (
              <button onClick={() => onOpenSellerProfile(listing)} type="button">
                Se sælgerprofil
              </button>
            )}
          </div>

          {!isOwner && isAuthenticated && listing.status === "active" && (
            <section className="safe-trade-card" aria-label="Tryg handel">
              <span className="mini-label">Tryg handel</span>
              <ul>
                <li>Hold aftaler og betaling i beskederne, så der er historik.</li>
                <li>Del ikke kortoplysninger, MitID eller engangskoder.</li>
                <li>Aftal kun afhentning og praktiske detaljer, når varen virker reel.</li>
              </ul>
            </section>
          )}

          {!isOwner && listing.status === "active" && (
            <div className="detail-actions buyer-actions">
              <button
                disabled={favoriteActionListingId === listing.id}
                onClick={() => onToggleFavorite(listing)}
                type="button"
              >
                {listing.isFavorite ? "Fjern favorit" : "Gem favorit"}
              </button>
              <button
                disabled={isContacting}
                onClick={() => onContactSeller(listing)}
                type="button"
              >
                {isContacting ? "Åbner beskeder..." : "Kontakt sælger"}
              </button>
              <button
                className="danger-secondary"
                disabled={isReportingListing}
                onClick={() => onReportListing(listing)}
                type="button"
              >
                {isReportingListing ? "Rapporterer..." : "Rapporter annonce"}
              </button>
              {contactError && <p className="status-line error">{contactError}</p>}
              {listingReportNotice && <p className="status-line notice">{listingReportNotice}</p>}
              {listingReportError && <p className="status-line error">{listingReportError}</p>}
            </div>
          )}

          {isOwner && (
            <div className="detail-actions">
              {(listing.status === "draft" || listing.status === "active") && (
                <button onClick={() => onEdit(listing)} type="button">
                  Rediger
                </button>
              )}
              {listing.status === "draft" && (
                <button onClick={() => onPublish(listing)} type="button">
                  Publicer
                </button>
              )}
              {listing.status === "active" && (
                <button onClick={() => onMarkSold(listing)} type="button">
                  Marker solgt
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function visibleSuggestions(suggestions, dismissedKeys) {
  return suggestions.filter(
    (suggestion) => !dismissedKeys.includes(`${suggestion.field}-${suggestion.message}`)
  );
}

function initials(name) {
  return (name || "S")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function RatingCheck({ label, status }) {
  return (
    <div>
      <span>{label}</span>
      <strong className={`rating-status ${status}`}>{statusLabelText(status)}</strong>
    </div>
  );
}

function sellerTrustText(listing) {
  if (!listing.ratingCount) {
    return listing.verified ? "Verificeret sælger uden rating endnu." : "Ny sælger uden rating endnu.";
  }

  return `${listing.ratingAverage.toFixed(1)} rating baseret på ${listing.ratingCount} handler.`;
}

function statusLabel(status) {
  const labels = {
    active: "Aktiv annonce",
    archived: "Arkiveret annonce",
    draft: "Kladde",
    sold: "Solgt annonce",
  };

  return labels[status] || "Annonce";
}

function statusLabelText(status) {
  const labels = {
    excellent: "Særdeles god",
    good: "God",
    missing: "Mangler",
    needs_more_detail: "Kan forbedres",
  };

  return labels[status] || status;
}

function fieldLabel(field) {
  const labels = {
    category: "Kategori",
    location: "Lokation",
    description: "Beskrivelse",
    images: "Billeder",
    price: "Pris",
    title: "Titel",
  };

  return labels[field] || field;
}

export default ListingDetailPage;
